import React from 'react';
import { formatDobInput, parseDobToDisplay } from '@/lib/formatters';
import { validateUkPhone } from '@/lib/validators';

interface CustomerDetails {
  name: string;
  email: string;
  phone: string;
  dob: string;
  jobTitle: string;
}

interface PatientDetailsFormProps {
  customerDetails: CustomerDetails;
  selectedCustomerId: string | null;
  dobTouched: boolean;
  onDobTouched: () => void;
  emailTouched: boolean;
  onEmailTouched: () => void;
  phoneTouched: boolean;
  onPhoneTouched: () => void;
  showDobError: boolean;
  showEmailError: boolean;
  showPhoneError: boolean;
  currentYear: number;
  onCustomerDetailsChange: (details: CustomerDetails) => void;
}

export const PatientDetailsForm: React.FC<PatientDetailsFormProps> = ({
  customerDetails,
  selectedCustomerId,
  dobTouched: _dobTouched,
  onDobTouched,
  emailTouched: _emailTouched,
  onEmailTouched,
  phoneTouched: _phoneTouched,
  onPhoneTouched,
  showDobError,
  showEmailError,
  showPhoneError,
  currentYear,
  onCustomerDetailsChange
}) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Name</label>
      <input
        className="w-full border rounded-lg p-2.5 text-sm bg-white"
        value={customerDetails.name}
        onChange={(e) => onCustomerDetailsChange({ ...customerDetails, name: e.target.value })}
        disabled={!!selectedCustomerId}
      />
    </div>
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Email Address</label>
      <input
        type="email"
        className="w-full border rounded-lg p-2.5 text-sm bg-white"
        value={customerDetails.email}
        onChange={(e) => onCustomerDetailsChange({ ...customerDetails, email: e.target.value })}
        onBlur={onEmailTouched}
        disabled={!!selectedCustomerId}
      />
      {showEmailError && (
        <p className="mt-1 text-xs font-semibold text-red-600">Enter a valid email address.</p>
      )}
    </div>
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
        Phone Number (optional)
      </label>
      <input
        className="w-full border rounded-lg p-2.5 text-sm bg-white"
        value={customerDetails.phone}
        onChange={(e) => onCustomerDetailsChange({ ...customerDetails, phone: e.target.value })}
        onBlur={(e) => {
          const value = e.target.value.trim();
          onPhoneTouched();
          const result = value ? validateUkPhone(value) : null;
          if (result?.valid)
            onCustomerDetailsChange({ ...customerDetails, phone: result.normalized });
        }}
        disabled={!!selectedCustomerId}
      />
      <p className="mt-1 text-[10px] text-slate-400">
        UK numbers only, e.g. 07123 456789 or +44 7123 456789.
      </p>
      {showPhoneError && (
        <p className="mt-1 text-xs font-semibold text-red-600">Enter a valid UK phone number.</p>
      )}
    </div>
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Date of Birth</label>
      <input
        type="text"
        inputMode="numeric"
        placeholder="DD-MM-YYYY"
        maxLength={10}
        className="w-full border rounded-lg p-2.5 text-sm bg-white"
        value={customerDetails.dob}
        onChange={(e) =>
          onCustomerDetailsChange({ ...customerDetails, dob: formatDobInput(e.target.value) })
        }
        onBlur={(e) => {
          const parsed = parseDobToDisplay(e.target.value);
          if (parsed && parsed !== customerDetails.dob) {
            onCustomerDetailsChange({ ...customerDetails, dob: parsed });
          }
          onDobTouched();
        }}
      />
      {showDobError && (
        <p className="mt-1 text-xs font-semibold text-red-600">
          Use DD-MM-YYYY. Year must be between 1900 and {currentYear}.
        </p>
      )}
    </div>
  </div>
);
